import { rectContains } from "../world/geometry";
import type { Rect, Vec2 } from "../world/geometry";
import type { EntityId } from "../world/Entity";

export type DragPhase = "idle" | "pending" | "dragging";

export interface DropTarget {
  readonly id: EntityId;
  readonly bounds: Rect;
}

export interface DragState {
  readonly phase: DragPhase;
  readonly entityId?: EntityId;
  readonly startPosition?: Vec2;
  readonly currentPosition?: Vec2;
}

export interface DragDropResult {
  readonly entityId: EntityId;
  readonly position: Vec2;
  readonly dropTarget?: DropTarget;
}

const DRAG_THRESHOLD_PX = 6;

/** Pure pointer-down/threshold/drag/drop-target/cancel state machine; no rendering. */
export class DragController {
  private state: DragState = { phase: "idle" };

  get current(): DragState {
    return this.state;
  }

  pointerDown(entityId: EntityId, position: Vec2): void {
    this.state = { phase: "pending", entityId, startPosition: position, currentPosition: position };
  }

  pointerMove(position: Vec2): DragState {
    if (this.state.phase === "idle") return this.state;
    if (this.state.phase === "pending") {
      const distance = Math.hypot(position.x - this.state.startPosition!.x, position.y - this.state.startPosition!.y);
      this.state =
        distance >= DRAG_THRESHOLD_PX
          ? { ...this.state, phase: "dragging", currentPosition: position }
          : { ...this.state, currentPosition: position };
      return this.state;
    }
    this.state = { ...this.state, currentPosition: position };
    return this.state;
  }

  pointerUp(dropTargets: readonly DropTarget[] = []): DragDropResult | undefined {
    const { phase, entityId, currentPosition } = this.state;
    let result: DragDropResult | undefined;
    if (phase === "dragging" && entityId && currentPosition) {
      const dropTarget = dropTargets.find((t) => rectContains(t.bounds, currentPosition));
      result = { entityId, position: currentPosition, ...(dropTarget ? { dropTarget } : {}) };
    }
    this.state = { phase: "idle" };
    return result;
  }

  cancel(): void {
    this.state = { phase: "idle" };
  }
}
